import { GraduationCap, MonitorPlay, Hammer, BookOpenText, Droplets, Trophy } from "lucide-react";
import { useReveal } from "../lib/useReveal";

// Each card uses a UNIQUE image directly matching the programme it depicts.
const PROGRAMS = [
  {
    icon: GraduationCap,
    title: "Quality Teachers",
    body: "Trained, dedicated tutors placed inside adopted classrooms — bringing consistency to schools long without it.",
    image: "https://lh3.googleusercontent.com/d/1KuAFlHRwz2PmxHOohmQaHlJPt2UmPgwY",
    alt: "A restored, well-lit rural classroom ready for teaching",
  },
  {
    icon: MonitorPlay,
    title: "Digital Learning & ETM Labs",
    body: "Education Technology Matters labs — computer education and smart classrooms that close the urban–rural learning gap.",
    image: "https://lh3.googleusercontent.com/d/1mkBYnLEu7eqkkghg-8jTSf7kX0pUXSEz",
    alt: "Rural students learning inside an ETM digital lab",
  },
  {
    icon: Hammer,
    title: "School Improvement Projects",
    body: "Restoring classrooms, repairing roofs, painting walls, waterproofing — the quiet infrastructure that lets learning happen.",
    image: "https://lh3.googleusercontent.com/d/1erlrArBIBWSJ2gCfjzDdgAJrUDJQ6Z8f",
    alt: "School restoration construction work in progress",
  },
  {
    icon: BookOpenText,
    title: "Educational Resources",
    body: "New desks, notebooks, textbooks, uniforms and 200+ laboratory kits — everything a child needs to arrive ready to learn.",
    image: "https://lh3.googleusercontent.com/d/1_Gr5EwiFq_UuUAyFNI0Smsl8vPXhge-4",
    alt: "New desks and learning resources inside a restored classroom",
  },
  {
    icon: Droplets,
    title: "Hygiene & Sanitation",
    body: "Hygienic toilets, filtered drinking water and safe sanitary blocks — the dignity work that keeps girls, especially, in school.",
    image: "https://lh3.googleusercontent.com/d/12N3KlWOONE8XPLH0oUi2KICeBgN48PYI",
    alt: "Freshly tiled floor of a rebuilt sanitation block",
  },
  {
    icon: Trophy,
    title: "Student Development",
    body: "Playgrounds, play equipment, sports, arts and confidence-building — an education that goes beyond the textbook.",
    image: "https://lh3.googleusercontent.com/d/12Li46aS7x8JwZOOy5locdbsadOnOp_j_",
    alt: "Rural school children playing at their village school",
  },
];

export default function Programs() {
  const ref = useReveal();

  return (
    <section id="programs" data-testid="programs-section" className="relative py-16 md:py-32 bg-cream-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal max-w-3xl text-center md:text-left mx-auto md:mx-0">
          <span className="section-chip">Our Work</span>
          <h2 className="mt-4 font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Our Areas of <em>Impact.</em>
          </h2>
          <p className="mt-4 text-navy/75 font-display text-base md:text-lg leading-relaxed">
            Six deliberate lines of work — each grounded in what rural Karnataka schools have asked us for, year after year.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROGRAMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={p.title}
                data-testid={`program-card-${i}`}
                className="group manuscript-card !p-0 overflow-hidden hover:-translate-y-2 transition-transform duration-300 flex flex-col"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-navy/10">
                  <img
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 w-11 h-11 rounded-full bg-white border-2 border-teal flex items-center justify-center shadow-lg">
                    <Icon className="w-5 h-5 text-teal" strokeWidth={1.9} />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display font-extrabold text-navy text-xl leading-snug">{p.title}</h3>
                  <p className="mt-2 text-navy/70 font-display text-sm leading-relaxed flex-1">{p.body}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
